<script setup lang="ts">

import { computed, nextTick, ref, watch } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { ECOMMERCE_ITEM_TAG, type EcommerceItem } from '@/types/shared/player-config'
import { T } from '@tolgee/vue'
import { useShop, useShopHelper } from '@/composables/game/shop'
import { formatDuration } from '@/helpers/date'
import { useAnalytics } from '@/composables/analytics'
import DefaultBOGOLabelImage from '@/assets/images/periodic/default-BOGO/default-BOGO-label.png'
import { twMerge } from 'tailwind-merge'

const props = defineProps<{
	name: string,
	description: string,
	image: string,
	ecommerceItems: EcommerceItem[]
	unopenedCount: number
	isLoading?: boolean
}>()

const emit = defineEmits<{
	(e: 'buy'): void
	(e: 'buyFree', item: EcommerceItem): void
	(e: 'open'): void
}>()

const { getItemDisabledSeconds } = useShopHelper()
const { triggerEvent } = useAnalytics()
const { isDiscount, saleValue, isDefaultBOGO, isBOGO, isDefaultBOGX, isBOGX } = useShop()

const contentContainer = ref<HTMLElement | null>(null)
const containerHeight = ref(0)

const freeItem = computed(() => props.ecommerceItems.find(item => !!(item.prices.find(price => price.amount === 0))))
const isFree = computed(() => !!freeItem.value)
const itemWithLowestPrice = computed((): EcommerceItem => {
  const mustItem = props.ecommerceItems.reduce<EcommerceItem | null>((acc, item) => {
    const getLowestPrice = (item: EcommerceItem) =>
      item.prices.reduce((min, price) => Math.min(min, price.amount), Infinity)

    return !acc || getLowestPrice(item) < getLowestPrice(acc) ? item : acc
  }, null)

  if (!mustItem) throw new Error('No item found with a price')
  return mustItem
})

const canHaveSale = computed(() => itemWithLowestPrice.value.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT))
const hasDiscount = computed(() => canHaveSale.value && isDiscount.value)
const hasBOGO = computed(() => canHaveSale.value && isBOGO.value)
const hasDefaultBOGO = computed(() => hasBOGO.value && isDefaultBOGO.value)
const disabledSeconds = computed(() => {
  if (!freeItem.value) return 0
  return getItemDisabledSeconds(freeItem.value)
})

function onOpenClick() {
  if (!props.unopenedCount) return
  emit('open')
}

function onBuyClick() {
  triggerEvent({ event: 'mystery_box_buy_clicked' })
  emit('buy')
}

watch(contentContainer, contentContainer => {
  nextTick(() => {
    if (!contentContainer) return
    containerHeight.value = contentContainer.clientHeight
  })
})
</script>

<template>
  <div
    class="w-full min-h-[164px] bg-mystery-box-container rounded-2xl border-2 border-[#545351] flex flex-col pt-[14px] pb-3 px-3"
  >
    <div class="w-full h-full flex-1 flex flex-col gap-y-1 inner-border relative">
      <div
        ref="contentContainer"
        class="flex-1 flex gap-y-2 gap-x-4"
      >
        <img
          :src="image"
          class="h-[100px] w-[100px] object-cover"
        >
        <div class="flex-1 flex flex-col gap-y-1.5">
          <span class="black-stroke text-[18px] leading-[28px] font-bold uppercase">{{ name }}</span>
          <span class="sm leading-4 font-bold">{{ description }}</span>
        </div>
      </div>
      <div
        class="w-full flex items-center gap-x-3"
      >
        <BaseButton
          v-if="disabledSeconds > 0"
          class="w-full !h-8 !rounded-[6px] !text-base"
          button-class="unslashed-zero whitespace-nowrap !border-white/40"
          disabled
          @click.stop
        >
          Next in {{ formatDuration(disabledSeconds) }}
        </BaseButton>
        <BaseButton
          v-else-if="isFree && !!freeItem"
          class="w-full !h-8 !rounded-[6px] !text-base"
          :button-class="hasDefaultBOGO ? 'default-BOGO-background-buttons border-0' : (isDefaultBOGX ? 'default-BOGX-background-buttons border-0' : '')"
          style-type="primary-pink"
          spinner-class="h-5 w-5 min-w-[20px]"
          :loading="isLoading"
          @click.stop="$emit('buyFree', freeItem)"
        >
          <T
            v-if="!isLoading"
            key-name="mystery_box_card.open_free_box"
            default-value="Open free box"
          />
        </BaseButton>
        <template v-else>
          <BaseButton
            class="flex-1 !px-0 !h-8 !rounded-[6px] !text-base"
            :button-class="hasDefaultBOGO ? 'default-BOGO-background-buttons border-0' : (isDefaultBOGX ? 'default-BOGX-background-buttons border-0' : '')"
            style-type="primary-pink"
            @click.stop="onBuyClick"
          >
            <div
              :class="twMerge('flex items-center h-full', hasBOGO ? 'gap-x-2' : (isBOGX ? 'gap-x-1 min-[400px]:gap-x-2' : 'gap-x-1'))"
            >
              <img
                v-if="hasBOGO"
                :src="DefaultBOGOLabelImage"
                class="h-4 w-auto"
              >
              <div
                v-else-if="isBOGX"
                class="text-sm min-[400px]:text-base min-[400px]:leading-5 font-semibold golden-stroke-black-text px-0.5 whitespace-nowrap"
              >
                Buy 1 Get {{ saleValue - 1 }}
              </div>
              <span
                v-if="hasBOGO || isBOGX"
                class="font-normal text-black/[24%]"
              >|</span>
              <span
                v-else-if="hasDiscount"
                class="text-xs line-through text-black/[44%]"
              >
                ${{ itemWithLowestPrice.prices[0].originalUsdAmount.toFixed(2) }}
              </span>

              <span v-else>
                <T
                  key-name="mystery_box_card.buy"
                  default-value="Buy"
                />
              </span>
              <span class="text-base">${{ itemWithLowestPrice.prices[0].usdAmount }}</span>
            </div>
          </BaseButton>
          <div
            class="flex-1 w-full"
            @click.stop="onOpenClick"
          >
            <BaseButton
              class="w-full !h-8 !rounded-[6px] !text-base"
              :class="{'opacity-[0.66] pointer-events-none': !unopenedCount}"
            >
              <div class="flex items-center gap-x-2">
                <div class="text-tropee-black">
                  <T
                    key-name="mystery_box_card.open"
                    default-value="Open"
                  />
                </div>
                <div class="w-px h-4 bg-tropee-black/[.24]" />
                <div class="text-tropee-black">
                  {{ unopenedCount }}
                </div>
              </div>
            </BaseButton>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bg-mystery-box-container {
	background-image: url('@/assets/images/mystery-box-card-bg.png');
	background-size: cover;
	background-repeat: no-repeat;
	background-blend-mode: overlay;
}

.black-stroke {
	text-shadow: -1.5px -1.5px 0 #000,
	1.5px -1.5px 0 #000,
	-1.5px 1.5px 0 #000,
	1.5px 1.5px 0 #000,
	-2px 0 0 #000,
	2px 0 0 #000,
	0 -2px 0 #000,
	0 2px 0 #000,
	-1px -1px 0 #000, /* Finer shadow adjustments */
	1px -1px 0 #000,
	-1px 1px 0 #000,
	1px 1px 0 #000;
}

.inner-border::before {
	content: "";
	position: absolute;
	top: -10px;
	left: -8px;
	right: -8px;
	bottom: -8px;
	border-radius: 20px; /* Ensure the same 12px border-radius */
	padding: 1px; /* Add padding to make the border visible */

	/* Initial gradient state */
	background: radial-gradient(
			ellipse at center,
			rgba(255, 255, 255, 0.8),
			rgba(255, 255, 255, 0.6) 40%,
			rgba(255, 255, 255, 0.2) 70%,
			transparent 85%
	);
	opacity: 1; /* Initial opacity */

	/* Clip to border only */
	-webkit-mask: linear-gradient(white 0 0) content-box, linear-gradient(white 0 0);
	mask: linear-gradient(white 0 0) content-box,
	linear-gradient(white 0 0);

	-webkit-mask-composite: xor;
	mask-composite: exclude;

	pointer-events: none; /* Allow interactions with the content */
	z-index: 1; /* Ensure it sits above the image but below the content */
}
</style>